import HubSpotLogo from "@/assets/integrations/hubspot.svg";
import { HubspotReconnectAlert } from "@/components/hubspot-reconnect-alert";
import PageSkeleton from "@/components/page-skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { useHubSpotIntegration } from "@/contexts/hubspot-integration-provider";
import { TeamUserRolesEnum } from "@/enums/team-user-role";
import useAccess from "@/hooks/useAccess";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { convertDateToDistanceNow } from "@/lib/date";
import { buildHubSpotOAuthUrl } from "@/lib/integrations";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle,
  ExternalLink,
  LogOut,
  RefreshCw,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

export default function IntegrationHubspotPage() {
  const api = useApi();
  const toastError = useToastError();
  const { toast } = useToast();
  const { t, currentLanguage } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasAccess } = useAccess();
  const { refetch: refetchHubSpotIntegration, hubspotIntegration } =
    useHubSpotIntegration();

  const [isLoading, setIsLoading] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [oauthWindow, setOauthWindow] = useState<Window | null>(null);

  const hasCorrectLicence = hasAccess({
    requiredLicences: ["professional"],
    licenceActiveState: true,
  });
  const hasRoleAccess = hasAccess({
    requiredRoles: [TeamUserRolesEnum.MANAGER, TeamUserRolesEnum.ADMIN],
  });
  const canConnectHubspot = hasCorrectLicence && hasRoleAccess;

  const isHubspotConnected = hubspotIntegration?.isActive === true;
  /**
   * The connection row is still there but HubSpot rejected its grant: nothing
   * syncs and the API refuses a manual sync until the user reconnects.
   */
  const hasExpiredAuth = isHubspotConnected && !hubspotIntegration.isAvailable;
  const isHubspotUsable = isHubspotConnected && !hasExpiredAuth;

  const createHubspotIntegration = async (code: string, state: string) => {
    try {
      setIsLoading(true);
      // `state` proves this code belongs to the flow this team started: the API
      // rejects the connection without it.
      await api.post("/integration/crm/hubspot", { code, state });

      // sync the integration
      await api.post("/integration/crm/hubspot/sync");

      // Refresh the HubSpot integration context
      await refetchHubSpotIntegration();

      toast({
        title: t("team.integrations.hubspot.success.title"),
        description: t("team.integrations.hubspot.success.description"),
      });
    } catch (error) {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnectHubspot = async () => {
    // Fetching the state puts a network round trip between the click and the
    // popup; without this guard a second click opens a second popup.
    if (isConnecting) return;

    // Opened blank before any await: browsers only allow a popup during the
    // click itself, and Safari withdraws that permission once a network
    // response has landed — `window.open` then returns null.
    const newWindow = window.open("about:blank", "_blank");
    if (!newWindow) {
      toastError();
      return;
    }

    try {
      setIsConnecting(true);
      // Opened server-side so the callback can prove the code came back from
      // the flow this team started, and not from a link someone else crafted.
      const { data } = await api.post<{ state: string }>(
        "/integration/crm/hubspot/oauth-state"
      );

      const hubspotOAuthUrl = buildHubSpotOAuthUrl(
        window.location.origin + window.location.pathname,
        data.state
      );
      if (!hubspotOAuthUrl) {
        newWindow.close();
        toastError();
        return;
      }

      newWindow.location.href = hubspotOAuthUrl;
      setOauthWindow(newWindow);
    } catch {
      newWindow.close();
      toastError();
    } finally {
      setIsConnecting(false);
    }
  };

  const handleSyncHubspot = async () => {
    try {
      setIsSyncing(true);
      await api.post("/integration/crm/hubspot/sync");

      // Refresh the HubSpot integration context
      await refetchHubSpotIntegration();

      toast({
        title: t("team.integrations.hubspot.sync.success.title"),
        description: t("team.integrations.hubspot.sync.success.description"),
      });
    } catch (error) {
      toastError();
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDisconnectHubspot = () => {
    setShowDisconnectDialog(true);
  };

  const confirmDisconnectHubspot = async () => {
    try {
      setIsDisconnecting(true);
      setShowDisconnectDialog(false);
      await api.delete("/integration/crm/hubspot");

      // Refresh the HubSpot integration context
      await refetchHubSpotIntegration();

      toast({
        title: t("team.integrations.hubspot.disconnect.title"),
        description: t("team.integrations.hubspot.disconnect.description"),
      });
    } catch (error) {
      toastError();
    } finally {
      setIsDisconnecting(false);
    }
  };

  useEffect(() => {
    const code = searchParams.get("code");
    // HubSpot echoes `state` back on the redirect, so the popup reads it from
    // the same place it reads the code — nothing has to be stashed in between.
    const state = searchParams.get("state") ?? "";
    if (code) {
      if (window.opener && !window.opener.closed) {
        createHubspotIntegration(code, state)
          .then(() => {
            window.opener.postMessage(
              { type: "HUBSPOT_OAUTH_SUCCESS" },
              window.location.origin
            );
            window.close();
          })
          .catch(() => {
            window.opener.postMessage(
              { type: "HUBSPOT_OAUTH_ERROR" },
              window.location.origin
            );
            window.close();
          });
      } else {
        createHubspotIntegration(code, state);
      }
    } else {
      refetchHubSpotIntegration();
    }
  }, [searchParams]);

  useEffect(() => {
    // `HubspotReconnectAlert` also renders on this page and listens for the
    // same messages for the popup it opened. Without this, one reconnect would
    // run both handlers: two refetches and a first-time-connect toast.
    if (!oauthWindow) return;

    const handleMessage = async (event: MessageEvent) => {
      if (event.data && event.data.type === "HUBSPOT_OAUTH_SUCCESS") {
        if (oauthWindow && !oauthWindow.closed) {
          oauthWindow.close();
        }
        setOauthWindow(null);

        // Refresh the HubSpot integration context
        await refetchHubSpotIntegration();

        toast({
          title: t("team.integrations.hubspot.success.title"),
          description: t("team.integrations.hubspot.success.description"),
        });
      } else if (event.data && event.data.type === "HUBSPOT_OAUTH_ERROR") {
        if (oauthWindow && !oauthWindow.closed) {
          oauthWindow.close();
        }
        setOauthWindow(null);

        toastError();
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [oauthWindow, t, toast, toastError, refetchHubSpotIntegration]);

  if (isLoading) {
    return <PageSkeleton className="p-4" />;
  }

  return (
    <div className="mt-8">
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/integrations")}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("common.back")}
        </Button>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-4 space-y-0">
          <div className="flex items-center gap-3">
            <img src={HubSpotLogo} alt="HubSpot" className="w-6 h-6" />
            <CardTitle>{t("team.integrations.hubspot.title")}</CardTitle>
            {hasExpiredAuth ? (
              <Badge variant="warning" className="text-xs">
                {t("team.integrations.hubspot.badge.expired")}
              </Badge>
            ) : isHubspotConnected ? (
              <Badge variant="success" className="text-xs">
                {t("team.integrations.hubspot.badge.connected")}
              </Badge>
            ) : (
              <Badge variant="outline" className="text-xs">
                {t("team.integrations.hubspot.badge.notConnected")}
              </Badge>
            )}
          </div>
          {isHubspotConnected && (
            <div className="flex items-center gap-3">
              {hubspotIntegration.lastSyncAt && (
                <span className="text-sm text-muted-foreground">
                  {t("team.integrations.hubspot.sync.lastSync")}{" "}
                  {convertDateToDistanceNow(
                    new Date(hubspotIntegration.lastSyncAt),
                    { languageOverride: currentLanguage }
                  )}
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleSyncHubspot}
                disabled={isSyncing || hasExpiredAuth}
                className="flex items-center gap-2"
              >
                <RefreshCw
                  className={`w-4 h-4 ${isSyncing ? "animate-spin" : ""}`}
                />
                {isSyncing
                  ? t("team.integrations.hubspot.sync.buttonLoading")
                  : t("team.integrations.hubspot.sync.button")}
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {!isHubspotConnected && (
            <>
              <p className="text-muted-foreground">
                {t("team.integrations.hubspot.status.description")}
              </p>

              <div className="flex items-center gap-3">
                <Button
                  onClick={handleConnectHubspot}
                  className="flex items-center gap-2"
                  disabled={!canConnectHubspot || isConnecting}
                >
                  <ExternalLink className="w-4 h-4" />
                  {t("team.integrations.hubspot.connect")}
                </Button>
                {!canConnectHubspot && !hasCorrectLicence ? (
                  <Badge
                    variant="secondary"
                    className="flex gap-1.5 items-center px-3 py-1"
                  >
                    <Zap className="w-3.5 h-3.5 text-purple-500" />
                    <span className="text-xs">
                      {t("team.integrations.hubspot.connectTooltip.proOnly")}
                    </span>
                  </Badge>
                ) : !canConnectHubspot && !hasRoleAccess ? (
                  <Badge
                    variant="secondary"
                    className="flex gap-1.5 items-center px-3 py-1"
                  >
                    <AlertTriangle className="w-3.5 h-3.5 text-orange-500" />
                    <span className="text-xs">
                      {t(
                        "team.integrations.hubspot.connectTooltip.memberRestriction"
                      )}
                    </span>
                  </Badge>
                ) : null}
              </div>
            </>
          )}

          {hasExpiredAuth && (
            <HubspotReconnectAlert translationPrefix="team.integrations.hubspot.auth" />
          )}

          {isHubspotConnected && (
            <div className="space-y-4">
              {isHubspotUsable && (
                <div className="flex items-start gap-4 p-4 border border-green-200 rounded-lg bg-green-50 dark:bg-green-950 dark:border-green-800">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5" />
                  <div className="flex-1">
                    <h3 className="mb-1 font-medium text-green-900 dark:text-green-100">
                      {t("team.integrations.hubspot.disconnect.connectedTitle")}
                    </h3>
                    <p className="text-sm text-green-700 dark:text-green-300">
                      {t(
                        "team.integrations.hubspot.disconnect.connectedDescription"
                      )}
                    </p>
                  </div>
                </div>
              )}
              <div className="flex justify-end">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDisconnectHubspot}
                  disabled={isDisconnecting}
                  className="flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  {isDisconnecting
                    ? t("team.integrations.hubspot.disconnect.buttonLoading")
                    : t("team.integrations.hubspot.disconnect.button")}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={showDisconnectDialog}
        onOpenChange={setShowDisconnectDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("team.integrations.hubspot.disconnect.confirmDialog.title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "team.integrations.hubspot.disconnect.confirmDialog.description"
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("team.integrations.hubspot.disconnect.confirmDialog.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDisconnectHubspot}
              disabled={isDisconnecting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDisconnecting
                ? t("team.integrations.hubspot.disconnect.buttonLoading")
                : t(
                    "team.integrations.hubspot.disconnect.confirmDialog.confirm"
                  )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
